import { Search } from "@/components/search/Search.tsx";
import { ColumnConfig, ColumnDefinition } from "@/types/column.types.ts";
import {
  Box,
  Button,
  Checkbox,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface AddMetricModalProps extends ModalProps {
  columnConfig: ColumnConfig;
  visibleColumns: string[];
  onSubmit: (updatedVisibleColumns: ColumnDefinition[]) => void;
  title?: string;
}

export function AddMetricModal({
  columnConfig,
  visibleColumns,
  onSubmit,
  title,
  ...props
}: AddMetricModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentVisibleColumns, setCurrentVisibleColumns] = useState(visibleColumns);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(
    columnConfig.categories.reduce<Record<string, boolean>>((acc, category) => {
      acc[category.id] = false;
      return acc;
    }, {})
  );

  const allColumns = useMemo(
    () => columnConfig.categories.flatMap((category) => category.columns),
    [columnConfig]
  );

  const matchedColumnIds = useMemo(() => {
    if (!searchTerm) return null;
    return new Set(
      allColumns
        .filter((column) => column.title?.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((column) => column.id)
    );
  }, [searchTerm, allColumns]);

  const isCategoryExpanded = (categoryId: string, columnIds: string[]) =>
    matchedColumnIds
      ? columnIds.some((columnId) => matchedColumnIds.has(columnId))
      : !!expandedCategories[categoryId];

  const mappedCurrentConfigColumns = useMemo(
    () =>
      currentVisibleColumns
        .map((columnId) => allColumns.find((column) => column.id === columnId))
        .filter(Boolean) as ColumnDefinition[],
    [currentVisibleColumns, allColumns]
  );

  const handleUpdate = () => {
    onSubmit(mappedCurrentConfigColumns);
    close(props.id);
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const handleColumnSelectionChange = (columnId: string, isSelected: boolean) => {
    setCurrentVisibleColumns((prev) =>
      isSelected ? [...prev, columnId] : prev.filter((colId) => colId !== columnId)
    );
  };

  return (
    <Modal
      {...props}
      variant={"middle"}
      size={"md"}
      title={title ?? t("reporting:people_analytics_add_metric")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      actions={
        <Box>
          <Button size={"lg"} onClick={handleUpdate} className="w-full">
            {t("common:ok")}
          </Button>
        </Box>
      }
    >
      <Stack gap="md">
        <Search
          value={searchTerm}
          placeholder={t("core:add_or_search_field") + "..."}
          clearable
          onClear={() => setSearchTerm("")}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {columnConfig.categories.map((category) => {
          const columnIds = category.columns.map((column) => column.id);
          const expanded = isCategoryExpanded(category.id, columnIds);
          return (
            <div key={category.id}>
              <Box
                className="flex items-center justify-between px-0.5 cursor-pointer font-semibold"
                onClick={() => toggleCategory(category.id)}
              >
                <Title size={"xs"} header={category.title} />
                <FontAwesomeIcon
                  icon={expanded ? "fa-light fa-chevron-down" : "fa-light fa-chevron-right"}
                />
              </Box>
              {expanded && (
                <Stack>
                  {category.columns
                    .filter((column) => (matchedColumnIds ? matchedColumnIds.has(column.id) : true))
                    .map((column) => (
                      <Stack
                        direction={"horizontal"}
                        gap={"md"}
                        key={column.id}
                        className={"py-2 px-0.5 border-b border-dark/5 last:border-b-0"}
                      >
                        <Checkbox
                          id={`Checkbox-${column.id}`}
                          checked={!!currentVisibleColumns.find((colId) => colId === column.id)}
                          onChange={(e) => handleColumnSelectionChange(column.id, e.target.checked)}
                          title={column.title}
                        />
                      </Stack>
                    ))}
                </Stack>
              )}
            </div>
          );
        })}
      </Stack>
    </Modal>
  );
}
